import { useEffect, useMemo, useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { dashboardStream } from "@/lib/ws"
import { useT } from "@/lib/i18n"
import { Badge, Button, Card, CardBody, Spinner, Toast } from "@/components/ui"
import { cn, timeAgo } from "@/lib/utils"
import type { ApprovalOut, AutonomyJobDetailOut, AutonomyJobOut, JobStatus } from "@/lib/types"
import {
  CheckIcon, XIcon, ShieldAlertIcon, BotIcon, RefreshCwIcon, Clock3Icon,
  CircleDotIcon, CircleCheckIcon, CircleXIcon, PauseCircleIcon, BanIcon,
  ChevronRightIcon, DollarSignIcon, RepeatIcon, TerminalIcon, WrenchIcon,
  MessageSquareIcon, FileTextIcon, LockIcon,
} from "lucide-react"

export const Route = createFileRoute("/_app/autonomy")({
  component: AutonomyPage,
})

type JobFilter = "all" | "active" | "done" | "failed"

const RISK_VARIANT: Record<string, "default" | "ok" | "warn" | "destructive" | "muted"> = {
  read: "muted", local_write: "default", external_write: "warn", destructive: "destructive", credential: "destructive",
}

/** Translate a server enum value through `prefix.<value>`; fall back to the raw value. */
function enumLabel(t: (k: string) => string, prefix: string, value: string, fallback = value): string {
  const key = `${prefix}.${value}`
  const s = t(key)
  return s === key ? fallback : s
}

function AutonomyPage() {
  const t = useT()
  const qc = useQueryClient()
  const [filter, setFilter] = useState<JobFilter>("active")
  const [openJob, setOpenJob] = useState<string | null>(null)
  const [toast, setToast] = useState<{ kind: "ok" | "destructive"; text: string } | null>(null)

  useEffect(() => {
    if (!toast) return
    const timer = setTimeout(() => setToast(null), 3500)
    return () => clearTimeout(timer)
  }, [toast])

  const approvals = useQuery({
    queryKey: ["approvals", "pending"],
    queryFn: () => api<ApprovalOut[]>("/api/autonomy/approvals", { query: { status: "pending" } }),
    refetchInterval: 30_000,
  })
  const jobs = useQuery({
    queryKey: ["autonomy-jobs"],
    queryFn: () => api<AutonomyJobOut[]>("/api/autonomy/jobs", { query: { limit: 100 } }),
    refetchInterval: (q) => {
      const live = (q.state.data ?? []).some((j) => j.status === "running" || j.status === "pending")
      return live ? 10_000 : 60_000
    },
  })

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["approvals"] })
    qc.invalidateQueries({ queryKey: ["autonomy-jobs"] })
    qc.invalidateQueries({ queryKey: ["autonomy-job"] })
  }

  // Live updates from the worker (events carry user_id, the WS already scopes them).
  useEffect(() => {
    return dashboardStream.subscribe((evt) => {
      if (["approval_requested", "proposal_created", "autonomy_job_finished"].includes(evt.type)) {
        invalidate()
        if (evt.type !== "autonomy_job_finished") {
          setToast({ kind: "ok", text: t("auto.toast.asking") })
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resolve = useMutation({
    mutationFn: (args: { id: string; approve: boolean }) =>
      api<ApprovalOut>(`/api/autonomy/approvals/${args.id}/resolve`, { method: "POST", body: { approve: args.approve } }),
    onSuccess: (ap) => {
      invalidate()
      setToast({ kind: "ok", text: ap.status === "approved" ? t("auto.toast.approved") : t("auto.toast.denied") })
    },
    onError: (e) => setToast({ kind: "destructive", text: (e as Error).message }),
  })
  const cancel = useMutation({
    mutationFn: (id: string) => api<AutonomyJobOut>(`/api/autonomy/jobs/${id}/cancel`, { method: "POST" }),
    onSuccess: () => { invalidate(); setToast({ kind: "ok", text: t("auto.toast.cancelled") }) },
    onError: (e) => setToast({ kind: "destructive", text: (e as Error).message }),
  })

  const visibleJobs = useMemo(() => {
    const all = jobs.data ?? []
    switch (filter) {
      case "active": return all.filter((j) => ["pending", "running", "waiting_approval", "waiting_local_agent"].includes(j.status))
      case "done": return all.filter((j) => j.status === "done")
      case "failed": return all.filter((j) => j.status === "failed" || j.status === "cancelled")
      default: return all
    }
  }, [jobs.data, filter])

  const pending = approvals.data ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">{t("auto.title")}</h1>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t("auto.subBefore")}{" "}
            <Link to="/projects" className="underline">{t("auto.subLink")}</Link> {t("auto.subAfter")}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={invalidate}>
          <RefreshCwIcon className={cn("h-3.5 w-3.5", (approvals.isFetching || jobs.isFetching) && "animate-spin")} /> {t("auto.refresh")}
        </Button>
      </div>

      {/* Approvals */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <ShieldAlertIcon className="h-4 w-4 text-gold" />
          <h2 className="text-sm font-semibold">{t("auto.needsApproval")}</h2>
          {pending.length > 0 && <Badge variant="warn">{pending.length}</Badge>}
        </div>
        {approvals.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner /> {t("auto.loading")}</div>
        ) : pending.length === 0 ? (
          <Card><CardBody>
            <div className="py-6 text-center text-sm text-muted-foreground">
              {t("auto.noApprovals")}
            </div>
          </CardBody></Card>
        ) : (
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))" }}>
            {pending.map((ap) => (
              <ApprovalCard
                key={ap.id}
                ap={ap}
                busy={resolve.isPending && resolve.variables?.id === ap.id}
                onApprove={() => resolve.mutate({ id: ap.id, approve: true })}
                onDeny={() => resolve.mutate({ id: ap.id, approve: false })}
                onOpenJob={ap.job_id ? () => setOpenJob(ap.job_id) : undefined}
              />
            ))}
          </div>
        )}
      </section>

      {/* Jobs */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <BotIcon className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold">{t("auto.runs")}</h2>
          <span className="mx-1 h-4 w-px bg-border" />
          {(["active", "all", "done", "failed"] as JobFilter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                "inline-flex h-7 items-center rounded-full border px-3 text-xs capitalize transition-colors",
                filter === f ? "border-primary bg-primary text-primary-foreground" : "bg-background hover:bg-accent",
              )}
            >
              {t(`auto.filter.${f}`)}
            </button>
          ))}
        </div>
        {jobs.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner /> {t("auto.loading")}</div>
        ) : visibleJobs.length === 0 ? (
          <Card><CardBody>
            <div className="py-6 text-center text-sm text-muted-foreground">
              {filter === "active" ? t("auto.noActiveRuns") : t("auto.noRuns")}
            </div>
          </CardBody></Card>
        ) : (
          <Card>
            <ul className="divide-y divide-border">
              {visibleJobs.map((j) => (
                <JobRow key={j.id} job={j} onOpen={() => setOpenJob(j.id)} onCancel={() => cancel.mutate(j.id)} />
              ))}
            </ul>
          </Card>
        )}
      </section>

      {openJob && (
        <JobDrawer
          id={openJob}
          onClose={() => setOpenJob(null)}
          onApprove={(id) => resolve.mutate({ id, approve: true })}
          onDeny={(id) => resolve.mutate({ id, approve: false })}
          onCancel={(id) => cancel.mutate(id)}
        />
      )}

      {toast && <Toast variant={toast.kind}>{toast.text}</Toast>}
    </div>
  )
}

/* ---------------------------------------------------------------- pieces */

function expiresIn(iso: string, t: (k: string, v?: Record<string, string | number>) => string): string {
  const ms = new Date(iso).getTime() - Date.now()
  if (ms <= 0) return t("auto.expired")
  const h = Math.floor(ms / 3_600_000), m = Math.floor((ms % 3_600_000) / 60_000)
  return h > 0 ? t("auto.leftHM", { h, m }) : t("auto.leftM", { m })
}

function ApprovalCard({ ap, busy, onApprove, onDeny, onOpenJob }: {
  ap: ApprovalOut; busy: boolean
  onApprove: () => void; onDeny: () => void; onOpenJob?: () => void
}) {
  const t = useT()
  const [showPayload, setShowPayload] = useState(false)
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-gold/25 bg-gold/[0.04] p-4 shadow-[0_0_0_1px_rgba(245,166,35,0.06)]">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant={RISK_VARIANT[ap.risk] ?? "muted"}>{enumLabel(t, "auto.risk", ap.risk, ap.risk.replace("_", " "))}</Badge>
          <span className="font-mono text-[11px] text-muted-foreground">{ap.action_kind}</span>
        </div>
        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <Clock3Icon className="h-3 w-3" /> {expiresIn(ap.expires_at, t)}
        </span>
      </div>
      <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed">{ap.summary}</p>
      <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
        <button className="underline-offset-2 hover:underline" onClick={() => setShowPayload((v) => !v)}>
          {showPayload ? t("auto.hidePayload") : t("auto.showPayload")}
        </button>
        {onOpenJob && (
          <button className="inline-flex items-center gap-1 underline-offset-2 hover:underline" onClick={onOpenJob}>
            {t("auto.openRun")} <ChevronRightIcon className="h-3 w-3" />
          </button>
        )}
        <span className="ml-auto">{timeAgo(ap.created_at)}</span>
      </div>
      {showPayload && (
        <pre className="max-h-48 overflow-auto rounded-lg border border-border bg-black/40 p-3 font-mono text-[11px] leading-relaxed text-foreground/90">
          {JSON.stringify(ap.staged_payload, null, 2)}
        </pre>
      )}
      <div className="flex gap-2">
        <Button size="sm" className="flex-1" onClick={onApprove} disabled={busy}>
          {busy ? <Spinner /> : <CheckIcon className="h-3.5 w-3.5" />} {t("auto.approve")}
        </Button>
        <Button size="sm" variant="outline" className="flex-1" onClick={onDeny} disabled={busy}>
          <XIcon className="h-3.5 w-3.5" /> {t("auto.deny")}
        </Button>
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: JobStatus }) {
  const t = useT()
  const map: Record<JobStatus, { v: "default" | "ok" | "warn" | "destructive" | "muted"; icon: React.ReactNode }> = {
    pending: { v: "muted", icon: <CircleDotIcon className="h-3 w-3" /> },
    running: { v: "default", icon: <Spinner className="h-3 w-3" /> },
    waiting_approval: { v: "warn", icon: <PauseCircleIcon className="h-3 w-3" /> },
    waiting_local_agent: { v: "muted", icon: <PauseCircleIcon className="h-3 w-3" /> },
    done: { v: "ok", icon: <CircleCheckIcon className="h-3 w-3" /> },
    failed: { v: "destructive", icon: <CircleXIcon className="h-3 w-3" /> },
    cancelled: { v: "muted", icon: <BanIcon className="h-3 w-3" /> },
  }
  const m = map[status] ?? map.pending
  const label = enumLabel(t, "auto.status", map[status] ? status : "pending")
  return <Badge variant={m.v} className="gap-1 normal-case tracking-normal">{m.icon}{label}</Badge>
}

function JobRow({ job, onOpen, onCancel }: { job: AutonomyJobOut; onOpen: () => void; onCancel: () => void }) {
  const t = useT()
  const cancellable = ["pending", "running", "waiting_approval", "waiting_local_agent"].includes(job.status)
  return (
    <li className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition hover:bg-foreground/[0.06]" onClick={onOpen}>
      <StatusBadge status={job.status} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13.5px] font-medium">{job.goal}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
          <span className="font-mono">{job.kind}</span>
          <span className="inline-flex items-center gap-1"><DollarSignIcon className="h-3 w-3" />{job.spent_usd.toFixed(2)} / {job.budget_usd.toFixed(2)}</span>
          <span className="inline-flex items-center gap-1"><RepeatIcon className="h-3 w-3" />{t("auto.turns", { used: job.turns_used, max: job.max_turns })}</span>
          <span>{timeAgo(job.finished_at || job.started_at || job.created_at)}</span>
        </div>
      </div>
      {cancellable && (
        <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); onCancel() }}>
          <BanIcon className="h-3.5 w-3.5" /> {t("auto.cancel")}
        </Button>
      )}
      <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
    </li>
  )
}

const STEP_ICON: Record<string, React.ReactNode> = {
  llm_turn: <MessageSquareIcon className="h-3.5 w-3.5" />,
  tool_call: <WrenchIcon className="h-3.5 w-3.5" />,
  local_exec: <TerminalIcon className="h-3.5 w-3.5" />,
  claude_cli: <TerminalIcon className="h-3.5 w-3.5" />,
  approval: <LockIcon className="h-3.5 w-3.5" />,
  report: <FileTextIcon className="h-3.5 w-3.5" />,
}

function JobDrawer({ id, onClose, onApprove, onDeny, onCancel }: {
  id: string; onClose: () => void
  onApprove: (approvalId: string) => void; onDeny: (approvalId: string) => void; onCancel: (jobId: string) => void
}) {
  const t = useT()
  const detail = useQuery({
    queryKey: ["autonomy-job", id],
    queryFn: () => api<AutonomyJobDetailOut>(`/api/autonomy/jobs/${id}`),
    refetchInterval: (q) => (q.state.data?.status === "running" || q.state.data?.status === "pending" ? 5_000 : false),
  })
  const approvals = useQuery({
    queryKey: ["approvals", "pending"],
    queryFn: () => api<ApprovalOut[]>("/api/autonomy/approvals", { query: { status: "pending" } }),
  })
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose])

  const job = detail.data
  const jobApprovals = (approvals.data ?? []).filter((a) => a.job_id === id)

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-[2px]" onClick={onClose}>
      <aside
        className="flex h-full w-full max-w-2xl animate-modal-in flex-col border-l border-border bg-[#12142a] shadow-[0_0_80px_rgba(0,0,0,0.6)]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 border-b border-border p-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {job && <StatusBadge status={job.status} />}
              <span className="font-mono text-[11px] text-muted-foreground">{job?.kind}</span>
            </div>
            <h2 className="mt-2 text-[15px] font-semibold leading-snug">{job?.goal ?? "…"}</h2>
            {job && (
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                <span>{t("auto.spentOf", { spent: job.spent_usd.toFixed(3), budget: job.budget_usd.toFixed(2) })}</span>
                <span>{t("auto.turns", { used: job.turns_used, max: job.max_turns })}</span>
                <span>{t("auto.started", { when: job.started_at ? timeAgo(job.started_at) : "—" })}</span>
                {job.finished_at && <span>{t("auto.finished", { when: timeAgo(job.finished_at) })}</span>}
              </div>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {job && ["pending", "running", "waiting_approval", "waiting_local_agent"].includes(job.status) && (
              <Button variant="outline" size="sm" onClick={() => onCancel(job.id)}><BanIcon className="h-3.5 w-3.5" /> {t("auto.cancel")}</Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} aria-label={t("auto.close")}><XIcon className="h-4 w-4" /></Button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          {detail.isLoading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner /> {t("auto.loading")}</div>}

          {jobApprovals.length > 0 && (
            <div className="mb-5 space-y-3">
              {jobApprovals.map((ap) => (
                <ApprovalCard key={ap.id} ap={ap} busy={false} onApprove={() => onApprove(ap.id)} onDeny={() => onDeny(ap.id)} />
              ))}
            </div>
          )}

          {job?.result?.summary && (
            <div className="mb-5 rounded-xl border border-mint/25 bg-mint/[0.06] p-4">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-mint">{t("auto.report")}</div>
              <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed">{String(job.result.summary)}</p>
            </div>
          )}
          {job?.error && (
            <div className="mb-5 rounded-xl border border-destructive/30 bg-destructive/[0.08] p-4 text-[13px] text-destructive">
              {job.error}
            </div>
          )}

          {job && (
            <ol className="relative ml-2 border-l border-border pl-5">
              {job.steps.length === 0 && <li className="text-sm text-muted-foreground">{t("auto.noSteps")}</li>}
              {job.steps.map((s) => {
                const isOpen = expanded.has(s.id)
                const tone = s.status === "error" ? "text-destructive" : s.status === "denied" ? "text-gold" : "text-muted-foreground"
                return (
                  <li key={s.id} className="relative pb-4">
                    <span className={cn(
                      "absolute -left-[27px] top-0.5 grid h-4 w-4 place-items-center rounded-full border bg-[#12142a]",
                      s.status === "error" ? "border-destructive text-destructive" :
                      s.status === "denied" ? "border-gold text-gold" :
                      s.status === "skipped" ? "border-border text-muted-foreground" : "border-primary/60 text-primary",
                    )}>
                      <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    </span>
                    <button className="flex w-full items-start gap-2 text-left" onClick={() => setExpanded((cur) => { const n = new Set(cur); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n })}>
                      <span className={cn("mt-0.5 shrink-0", tone)}>{STEP_ICON[s.kind] ?? <CircleDotIcon className="h-3.5 w-3.5" />}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-medium leading-snug">{s.title}</span>
                        <span className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-muted-foreground">
                          <span className="font-mono">#{s.seq} · {s.kind}</span>
                          {s.status !== "ok" && <span className={tone}>{enumLabel(t, "auto.step", s.status)}</span>}
                          {s.usd > 0 && <span>${s.usd.toFixed(3)}</span>}
                          {s.duration_ms != null && <span>{(s.duration_ms / 1000).toFixed(1)}s</span>}
                          <span>{timeAgo(s.created_at)}</span>
                        </span>
                      </span>
                      <ChevronRightIcon className={cn("mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform", isOpen && "rotate-90")} />
                    </button>
                    {isOpen && (
                      <div className="mt-2 grid gap-2">
                        {Object.keys(s.input).length > 0 && (
                          <pre className="max-h-56 overflow-auto rounded-lg border border-border bg-black/40 p-3 font-mono text-[11px] leading-relaxed">
                            {JSON.stringify(s.input, null, 2)}
                          </pre>
                        )}
                        {Object.keys(s.output).length > 0 && (
                          <pre className="max-h-72 overflow-auto rounded-lg border border-border bg-black/40 p-3 font-mono text-[11px] leading-relaxed">
                            {JSON.stringify(s.output, null, 2)}
                          </pre>
                        )}
                      </div>
                    )}
                  </li>
                )
              })}
            </ol>
          )}
        </div>
      </aside>
    </div>
  )
}
